import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import TrustBadge from '../components/TrustBadge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    shopName: '',
    inquiryType: '',
    message: '',
    contactMethod: '',
    preferredTime: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'お名前を入力してください';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'ご相談内容を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        // APIエンドポイントにPOSTリクエスト
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${BACKEND_URL}/api/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          // Show success toast
          toast.success(result.message || 'お問い合わせありがとうございます。内容を確認のうえ、通常24時間以内にご返信いたします。');

          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            shopName: '',
            inquiryType: '',
            message: '',
            contactMethod: '',
            preferredTime: ''
          });
        } else {
          throw new Error(result.detail || 'お問い合わせの送信に失敗しました。');
        }
      } catch (error) {
        console.error('送信エラー:', error);
        toast.error(error.message || 'お問い合わせの送信に失敗しました。もう一度お試しください。');
      }
    } else {
      toast.error('必須項目をご確認ください。');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-spacing bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 fade-in">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#2C2C2C' }}>
              お問い合わせ
            </h1>
            <p className="text-xl text-gray-700 mb-6">
              お気軽にご相談ください。強引な営業は一切ありません。
            </p>
            <TrustBadge />
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="md:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <Mail className="w-8 h-8 mb-3" style={{ color: '#F7C9D3' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#2C2C2C' }}>メール</h3>
                <p className="text-gray-700 text-sm">info@sakura-local.jp</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <MapPin className="w-8 h-8 mb-3" style={{ color: '#F7C9D3' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#2C2C2C' }}>所在地</h3>
                <p className="text-gray-700 text-sm">兵庫県赤穂郡上郡町</p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <MapPin className="w-8 h-8 mb-3" style={{ color: '#F7C9D3' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#2C2C2C' }}>対応エリア</h3>
                <p className="text-gray-700 text-sm">
                  上郡町・赤穂市・たつの市・相生市
                </p>
                <p className="text-xs text-gray-500 mt-2">※ Webサイト制作・ホスティングのみの場合は、SNS経由で全国対応しています。</p>
              </div>

              <div className="bg-[#F7C9D3] rounded-2xl p-6">
                <h3 className="font-semibold mb-3" style={{ color: '#2C2C2C' }}>送信後の流れ</h3>
                <p className="text-sm" style={{ color: '#2C2C2C' }}>
                  1～2営業日以内にご連絡いたします。<br />
                  無料ヒアリングでお話をお伺いいたします。
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <p className="text-sm text-gray-600 mb-2">
                  最新の取り組みはInstagramでもご紹介しています。
                </p>
                <a 
                  href="https://www.instagram.com/sakura.local" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-gray-700 hover:text-[#F7C9D3] transition-colors"
                >
                  <Instagram size={16} className="mr-2" />
                  @sakura.local
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-600 leading-relaxed">
                  お問い合わせ内容を確認のうえ、通常2〜3営業日以内にご返信しています。<br className="hidden md:inline" />
                  内容によっては、お時間をいただく場合があります。
                </p>
              </div>
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name">お名前<span className="text-red-500">*</span></Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={errors.name ? 'border-red-500' : ''}
                      placeholder="山田太郎"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email">メールアドレス<span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'border-red-500' : ''}
                      placeholder="example@email.com"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone">電話番号（任意）</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="090-1234-5678"
                    />
                  </div>

                  {/* Shop Name */}
                  <div>
                    <Label htmlFor="shopName">店舗名（任意）</Label>
                    <Input
                      id="shopName"
                      name="shopName"
                      value={formData.shopName}
                      onChange={handleChange}
                      placeholder="例：さくらカフェ"
                    />
                  </div>

                  {/* Inquiry Type */}
                  <div>
                    <Label htmlFor="inquiryType">お問い合わせ項目</Label>
                    <Select value={formData.inquiryType} onValueChange={(value) => setFormData(prev => ({ ...prev, inquiryType: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">Webサイト制作について</SelectItem>
                        <SelectItem value="sns">SNS運用について</SelectItem>
                        <SelectItem value="livestream">TikTok LIVE・ライブ配信について</SelectItem>
                        <SelectItem value="marketing">マーケティングについて</SelectItem>
                        <SelectItem value="consulting">コンサルティングについて</SelectItem>
                        <SelectItem value="maintenance">Webサイトの保守・管理について</SelectItem>
                        <SelectItem value="hosting">ホスティングプランについて</SelectItem>
                        <SelectItem value="other">その他</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message">ご相談内容<span className="text-red-500">*</span></Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={errors.message ? 'border-red-500' : ''}
                      placeholder="ご相談内容やご質問をご記入ください"
                      rows={5}
                    />
                    {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                  </div>

                  {/* Contact Method */}
                  <div>
                    <Label htmlFor="contactMethod">希望連絡方法</Label>
                    <Select value={formData.contactMethod} onValueChange={(value) => setFormData(prev => ({ ...prev, contactMethod: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">メール</SelectItem>
                        <SelectItem value="phone">電話</SelectItem>
                        <SelectItem value="any">どちらでも</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Preferred Time */}
                  <div>
                    <Label htmlFor="preferredTime">希望連絡時間帯</Label>
                    <Select value={formData.preferredTime} onValueChange={(value) => setFormData(prev => ({ ...prev, preferredTime: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">午前（9:00-12:00）</SelectItem>
                        <SelectItem value="afternoon">午後（13:00-17:00）</SelectItem>
                        <SelectItem value="evening">夕方以降（17:00-20:00）</SelectItem>
                        <SelectItem value="any">いつでも</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full btn-primary py-6 text-lg"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    送信する
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;